import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Calendar } from "@/components/ui/calendar";
import { MapPin, Clock, StickyNote, CalendarDays } from "lucide-react";
import { api } from "@/lib/api";

function toKey(d) {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function ScheduleSection() {
  const [selected, setSelected] = useState(new Date());
  const [entries, setEntries] = useState([]);
  const [eventDays, setEventDays] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/public/schedules").then((res) => {
      const days = res.data.map((e) => {
        const [y, m, d] = e.date.split("-").map(Number);
        return new Date(y, m - 1, d);
      });
      setEventDays(days);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selected) return;
    setLoading(true);
    api
      .get("/public/schedule", { params: { date: toKey(selected) } })
      .then((res) => setEntries(res.data))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }, [selected]);

  const niceDate = selected
    ? selected.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })
    : "";

  return (
    <section id="schedule" className="py-20 md:py-32 bg-sc-cream">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl"
        >
          <div className="eyebrow mb-4">Plan your scoop</div>
          <h2 className="font-serif-display text-4xl sm:text-5xl tracking-tight text-sc-espresso">
            Where Will Our <em className="italic font-light text-sc-bordeaux">Gelato Cart</em> Be?
          </h2>
          <p className="mt-5 text-base md:text-lg text-sc-espresso/75 leading-relaxed">
            Pick a date and we'll show you exactly where to find the sweetest cart in New York.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12 mt-12 items-start">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="md:col-span-5 bg-white rounded-3xl p-4 sm:p-6 shadow-[0_8px_30px_rgba(44,42,40,0.06)] border border-sc-gold/15 flex justify-center"
            data-testid="schedule-calendar"
          >
            <Calendar
              mode="single"
              selected={selected}
              onSelect={(d) => d && setSelected(d)}
              modifiers={{ event: eventDays }}
              modifiersClassNames={{ event: "has-event relative" }}
              className="rounded-xl"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="md:col-span-7"
          >
            <div className="flex items-center gap-2 text-sc-gold mb-5">
              <CalendarDays size={18} />
              <span className="font-serif-display text-2xl text-sc-espresso">{niceDate}</span>
            </div>

            {loading ? (
              <p className="text-sc-espresso/60">Loading…</p>
            ) : entries.length === 0 ? (
              <div className="rounded-3xl border border-dashed border-sc-espresso/20 p-10 text-center bg-sc-blue/30" data-testid="schedule-empty">
                <p className="font-serif-display text-2xl text-sc-espresso">No cart stop this day.</p>
                <p className="mt-2 text-sc-espresso/70">
                  Try another date — or book us for your own private event below.
                </p>
              </div>
            ) : (
              <div className="space-y-5" data-testid="schedule-entries">
                {entries.map((e) => (
                  <div
                    key={e.id}
                    className="bg-white rounded-3xl p-7 shadow-[0_8px_30px_rgba(44,42,40,0.06)] border border-sc-gold/15"
                  >
                    <h3 className="font-serif-display text-2xl text-sc-espresso flex items-start gap-2">
                      <MapPin className="text-sc-sage mt-1 shrink-0" size={22} />
                      {e.location_name}
                    </h3>
                    {e.address && <p className="mt-1 ml-7 text-sc-espresso/60 text-sm">{e.address}</p>}
                    <div className="mt-4 ml-7 flex flex-wrap gap-x-8 gap-y-3 text-sc-espresso/80">
                      {(e.start_time || e.end_time) && (
                        <span className="flex items-center gap-2">
                          <Clock size={16} className="text-sc-gold" />
                          {e.start_time} {e.end_time && `– ${e.end_time}`}
                        </span>
                      )}
                      {e.notes && (
                        <span className="flex items-center gap-2 italic">
                          <StickyNote size={16} className="text-sc-gold" />
                          {e.notes}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
